// Verhalten der Bento-Karten (snippets/cb-bento-card.liquid), von allen Bento-Sections geteilt.
// - Tippen auf eine Bildkarte oeffnet ihren Text, eine zweite Kachel schliesst die erste.
// - Karten mit data-cb-bento-dialog oeffnen stattdessen per Klick/Tippen ihren <dialog-component>
//   (assets/dialog.js); Hover- und Scroll-Aufklappen bleiben.
// - Ohne Hover (Touch) oder unter 750 px klappt der Text stufenlos mit dem Scrollen auf:
//   je weiter die Karte nach oben wandert, desto weiter, und beim Verlassen oben wieder zu.
// Grenzwerte als Anteil der Viewport-Hoehe. Aufbau wie urspruenglich (Oberkante oeffnet, Unterkante
// schliesst, breite Rampen = ruckelfrei), Werte nach Paul (05.10.2026): Text kommt spaeter als frueher,
// das Bild ist in der Bildschirmmitte noch ganz zu sehen.
const CB_BENTO_OPEN_FROM = 0.45; // Oberkante hier: Text noch ganz zu
const CB_BENTO_OPEN_TO = 0.25; // Oberkante hier: Text ganz offen
const CB_BENTO_CLOSE_FROM = 0.34; // Unterkante (ohne Text) hier: Text beginnt wieder zuzugehen
const CB_BENTO_CLOSE_TO = 0.14; // Unterkante (ohne Text) hier: Text wieder ganz zu

class CbBentoGrid extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cbInit) return;
    this.dataset.cbInit = '1';

    this.cards = Array.from(this.querySelectorAll('[data-cb-bento-reveal]'));
    if (!this.cards.length && !this.querySelector('[data-cb-bento-dialog]')) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.setupScrollReveal();
  }

  disconnectedCallback() {
    delete this.dataset.cbInit;
    if (!this.onClick) return;
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.teardownScrollReveal();
    if (this.narrowQuery && this.onNarrowChange && this.narrowQuery.removeEventListener) {
      this.narrowQuery.removeEventListener('change', this.onNarrowChange);
    }
    this.onNarrowChange = null;
  }

  setState(card, open) {
    card.classList.toggle('is-open', open);
    if (card.hasAttribute('data-cb-bento-toggle')) card.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  onClick(event) {
    const dialogCard = event.target && event.target.closest ? event.target.closest('[data-cb-bento-dialog]') : null;
    if (dialogCard && this.contains(dialogCard)) {
      const dialog = document.getElementById(dialogCard.getAttribute('data-cb-bento-dialog'));
      if (dialog && typeof dialog.showDialog === 'function') dialog.showDialog();
      return;
    }

    const card = event.target && event.target.closest ? event.target.closest('[data-cb-bento-toggle]') : null;
    if (!card || !this.contains(card)) return;

    const isOpen = card.classList.contains('is-open');
    card.classList.remove('is-scrub');
    this.cards.forEach((other) => {
      if (other !== card && !other.classList.contains('is-scrub')) this.setState(other, false);
    });
    this.setState(card, !isOpen);
    // Manueller Zustand gewinnt, bis die Karte einmal ganz aus dem Viewport war.
    card.setAttribute('data-cb-manual', '');
  }

  onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    const card = event.target && event.target.closest ? event.target.closest('[data-cb-bento-toggle]') : null;
    if (card) this.setState(card, false);
  }

  setupScrollReveal() {
    if (this.dataset.scrollReveal === 'false') return;
    if (typeof window.matchMedia !== 'function' || typeof window.IntersectionObserver !== 'function') return;
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.narrowQuery = window.matchMedia('(max-width: 749px)');
    this.reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.onNarrowChange) {
      this.onNarrowChange = () => {
        this.teardownScrollReveal();
        this.setupScrollReveal();
      };
      if (this.narrowQuery.addEventListener) this.narrowQuery.addEventListener('change', this.onNarrowChange);
    }
    if (this.pointerQuery.matches && !this.narrowQuery.matches) return;

    this.scrollProgress = new WeakMap();
    this.scrollFrame = 0;
    this.nearObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.bindScroll();
            this.evaluateCards();
          } else {
            // Letzter Durchlauf, damit keine Karte offen aus dem Bild faehrt.
            this.evaluateCards();
            this.unbindScroll();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );
    this.nearObserver.observe(this);
  }

  teardownScrollReveal() {
    this.unbindScroll();
    if (this.nearObserver) this.nearObserver.disconnect();
    this.nearObserver = null;
    this.scrollProgress = null;
    this.cards.forEach((card) => {
      if (!card.classList.contains('is-scrub')) return;
      card.classList.remove('is-scrub');
      this.setState(card, false);
    });
  }

  bindScroll() {
    if (this.scrollBound) return;
    this.scrollBound = true;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  unbindScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = 0;
    if (!this.scrollBound) return;
    this.scrollBound = false;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  // Auf einen Frame gedrosselt, damit Scrollen fluessig bleibt.
  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.evaluateCards();
    });
  }

  evaluateCards() {
    if (!this.scrollProgress) return;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!vh) return;
    const ramp = (value, from, to) => Math.min(1, Math.max(0, (value - from) / (to - from)));
    const reduced = this.reducedQuery.matches;

    // Erst alles messen, dann schreiben, damit pro Frame nur ein Layout entsteht.
    // Gerechnet wird mit der Kartenhoehe ohne Text: Waechst die Karte beim Aufklappen,
    // soll das den eigenen Fortschritt nicht verschieben.
    const updates = this.cards.map((card) => {
      const rect = card.getBoundingClientRect();
      const reveal = card.querySelector('.cb-bento-card__reveal');
      const text = reveal ? reveal.querySelector('.cb-bento-card__text') : null;
      const ratio = card.querySelector('.cb-bento-card__ratio');
      const revealHeight = reveal ? reveal.offsetHeight : 0;
      const closedHeight = Math.max(ratio ? ratio.offsetHeight : 0, rect.height - revealHeight);
      return {
        card,
        top: rect.top,
        bottom: rect.top + closedHeight,
        textHeight: text ? text.scrollHeight : 0,
      };
    });

    updates.forEach(({ card, top, bottom, textHeight }) => {
      if (bottom < 0 || top > vh) card.removeAttribute('data-cb-manual');
      if (card.hasAttribute('data-cb-manual')) return;

      const raw = Math.min(
        ramp(top / vh, CB_BENTO_OPEN_FROM, CB_BENTO_OPEN_TO),
        ramp(bottom / vh, CB_BENTO_CLOSE_TO, CB_BENTO_CLOSE_FROM)
      );
      // Weich ein- und auslaufen; bei reduzierter Bewegung nur auf oder zu.
      let progress = reduced ? (raw >= 0.5 ? 1 : 0) : raw * raw * (3 - 2 * raw);
      progress = Math.round(progress * 200) / 200;

      const wasScrub = card.classList.contains('is-scrub');
      if (wasScrub && this.scrollProgress.get(card) === progress) return;
      this.scrollProgress.set(card, progress);

      card.style.setProperty('--cb-bento-p', progress);
      card.style.setProperty('--cb-bento-h', textHeight + 'px');
      if (!wasScrub) card.classList.add('is-scrub');
      this.setState(card, progress >= 0.5);
    });
  }
}

if (!customElements.get('cb-bento-grid')) {
  customElements.define('cb-bento-grid', CbBentoGrid);
}
